import type { MiniGame, MiniHost, MiniResult, SfxName } from './types';
import { div, btn } from './kit';
import { tr, lang } from '@/i18n';
import { bus } from '@/core/bus';

/**
 * v1.0 作業視窗：小遊戲開在這裡。規則（照企劃）：
 * - 一定要「自己按右上角 ✕」才會關閉（點旁邊、按 Esc 都不會關）
 * - 還沒做完就按 ✕：會先問「確定離開？這次的進度不會保留」
 * - 做完會顯示結果；按 ✕ 關閉後才把結果交回任務
 */
export interface WorkOpts {
  game: MiniGame;
  title: string;
  icon: string;
  params: Record<string, unknown>;
  seed: number;
  roleId: string;
  onClose: (r: MiniResult | null) => void;
}

/** mulberry32：可重現的亂數 */
function rngFrom(seed: number): () => number {
  let a = seed >>> 0;
  return () => { a = (a + 0x6d2b79f5) >>> 0; let t = a; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}

export function openWork(o: WorkOpts): () => void {
  const timers: number[] = []; const cleanups: (() => void)[] = [];
  let result: MiniResult | null = null; let closed = false;
  const app = document.getElementById('app') ?? document.body;
  const statusEl = div('ww-status');
  const closeBtn = btn('✕', () => askClose(), 'ww-x'); closeBtn.setAttribute('aria-label', tr("關閉作業視窗"));
  const side = div('ww-side');
  const stage = div('ww-stage');
  const flashEl = div('ww-flash');
  // v1.1.1 說明欄可以收起來（手機橫放時說明欄蓋在畫面上，看完收起）
  const sideBtn = btn(tr("📖 說明"), () => root.classList.toggle('side-off'), 'ww-sidebtn');
  // v1.2.2 畫面縮放：小螢幕自動縮小作業區（東西變小、空間變大）；也可以按 🔍− / 🔍+ 自己調（會重新開始這個作業）
  const zoomOut = btn('🔍−', () => setZoom(-1), 'ww-zbtn'); zoomOut.setAttribute('aria-label', tr("縮小作業畫面"));
  const zoomIn = btn('🔍+', () => setZoom(1), 'ww-zbtn'); zoomIn.setAttribute('aria-label', tr("放大作業畫面"));
  const wrap = div('ww-stage-wrap', {}, stage, flashEl);
  const panel = div('ww-panel', {}, div('ww-head', {}, div('ww-title', {}, `${o.icon} ${o.title}`), statusEl, zoomOut, zoomIn, sideBtn, closeBtn), div('ww-body', {}, side, wrap));
  const root = div('ww', {}, panel);
  root.setAttribute('role', 'dialog'); root.setAttribute('aria-modal', 'true'); root.setAttribute('aria-label', o.title);
  root.addEventListener('pointerdown', e => e.stopPropagation());
  root.addEventListener('keydown', e => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); } });
  app.append(root);

  const close = () => {
    if (closed) return; closed = true;
    timers.forEach(t => clearInterval(t)); cleanups.forEach(f => { try { f(); } catch { /* ignore */ } });
    root.remove(); o.onClose(result);
  };
  const askClose = () => {
    if (result) return close();
    if (panel.querySelector('.ww-confirm')) return;
    const box = div('ww-confirm', {}, div('ww-confirm-text', {}, tr("作業還沒完成，確定要關閉嗎？這次的進度不會保留（可以再來一次）。")),
      div('ww-confirm-row', {}, btn(tr("繼續作業"), () => box.remove(), 'mg-btn primary'), btn(tr("關閉"), () => close(), 'mg-btn')));
    panel.append(box);
  };
  let flashT = 0;
  const host: MiniHost = {
    stage, side,
    size: () => ({ w: stage.clientWidth, h: stage.clientHeight }),
    status: t => { statusEl.textContent = t; },
    flash: (t, tone = 'info') => { flashEl.textContent = t; flashEl.className = `ww-flash show ${tone}`; clearTimeout(flashT); flashT = window.setTimeout(() => (flashEl.className = 'ww-flash'), 1600); },
    sfx: (n: SfxName) => bus.emit('sfx', n),
    rng: rngFrom(o.seed),
    every: (ms, fn) => { timers.push(window.setInterval(() => { if (!closed && !result) fn(); }, ms)); },
    after: (ms, fn) => { timers.push(window.setTimeout(() => { if (!closed) fn(); }, ms)); },
    onCleanup: fn => cleanups.push(fn),
    finish: r => {
      if (result) return; result = r;
      bus.emit('sfx', r.ok ? 'ok' : 'warn');
      const lines = (r.lines ?? []).map(l => div('ww-res-line', {}, l));
      const res = div('ww-result ' + (r.ok ? 'ok' : 'bad'), {},
        div('ww-res-big', {}, r.ok ? (r.score >= 0.8 ? tr("✅ 完成！做得很好") : tr("✅ 完成")) : tr("❌ 沒有完成")),
        div('ww-res-sum', {}, r.summary),
        r.achievement ? div('ww-res-ach', {}, `🏆 ${r.achievement}`) : null,
        ...lines,
        div('ww-res-score', {}, tr("評分：{0} 分", Math.round(r.score * 100))),
        div('ww-res-hint', {}, tr("按右上角 ✕ 關閉作業視窗")));
      panel.append(res);
      closeBtn.classList.add('pulse');
    },
    params: o.params, roleId: o.roleId, lang,
  };
  // 縮放倍率：自動（依作業區大小）× 玩家自己調的倍率（記在這台裝置）
  const STEPS = [0.6, 0.7, 0.8, 0.9, 1, 1.15, 1.3];
  const userStep = () => { try { const v = Number(localStorage.getItem('ffg-ww-zoom')); return STEPS.includes(v) ? v : 1; } catch { return 1; } };
  const scaleNow = () => {
    const W = wrap.clientWidth, H = wrap.clientHeight;
    const auto = Math.max(0.5, Math.min(1, H / 470, W / 760));
    return Math.max(0.4, Math.min(1.3, auto * userStep()));
  };
  const mount = () => {
    if (closed) return;
    timers.splice(0).forEach(t => { clearInterval(t); clearTimeout(t); }); cleanups.splice(0).forEach(f => { try { f(); } catch { /* ignore */ } });
    result = null; panel.querySelector('.ww-result')?.remove(); closeBtn.classList.remove('pulse');
    stage.textContent = ''; side.textContent = ''; statusEl.textContent = '';
    const k = scaleNow();
    const W = wrap.clientWidth, H = wrap.clientHeight;
    Object.assign(stage.style, k === 1 ? { width: '', height: '', transform: '', right: '', bottom: '' } : { width: `${W / k}px`, height: `${H / k}px`, transform: `scale(${k})`, transformOrigin: '0 0', right: 'auto', bottom: 'auto' });
    side.style.zoom = k < 1 ? String(Math.max(0.75, k)) : '';
    host.rng = rngFrom(o.seed + mounts++ * 7);
    try { o.game.mount(host); } catch (e) { console.error(e); host.finish({ ok: false, score: 0, summary: tr("這個作業出了點問題，請回報給開發者。") }); }
  };
  let mounts = 0;
  const setZoom = (d: number) => {
    const i = Math.max(0, Math.min(STEPS.length - 1, STEPS.indexOf(userStep()) + d));
    try { localStorage.setItem('ffg-ww-zoom', String(STEPS[i])); } catch { /* ignore */ }
    if (result) { mount(); return; }
    host.flash(tr("畫面大小改了，作業重新開始"), 'info'); mount();
  };
  // 等版面算好再開始（stage 才有大小）
  requestAnimationFrame(() => requestAnimationFrame(() => mount()));
  closeBtn.focus({ preventScroll: true });
  return close;
}
